import { useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Bell } from '@/icons/Icon';
import { NotificationDropdown } from './NotificationDropdown';
import {
  UNREAD_COUNT_DISPLAY_CAP,
  useNotificationBumpSync,
  useUnreadCountQuery,
} from './queries';

/**
 * Topbar notification bell: a live unread badge + a popover of recent
 * notifications. Rendered once inside the shared `Topbar` organism so it
 * appears app-wide. The badge count is server-authoritative (`unread-count`
 * query); `useNotificationBumpSync` refetches it whenever the per-user
 * Firestore doc bumps — except once the count is already past
 * `UNREAD_COUNT_DISPLAY_CAP`, where further bumps stop triggering a refetch
 * (see the hook's doc comment). Both are gated on an access token, so
 * unauthed frames (and component tests without auth) fire no request.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  useNotificationBumpSync();
  const count = useUnreadCountQuery().data?.count ?? 0;
  const displayCount =
    count > UNREAD_COUNT_DISPLAY_CAP ? `${UNREAD_COUNT_DISPLAY_CAP}+` : count;
  const label =
    count > 0
      ? `Open notifications, ${displayCount} unread`
      : 'Open notifications';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="icon"
          aria-label={label}
          className="relative"
        >
          <Bell />
          {count > 0 && (
            <span
              className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
              aria-hidden
            >
              {displayCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0" sideOffset={8}>
        <NotificationDropdown onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
